// reflects Stripes subscription status 1:1 for now
import { endOfMonth, endOfYear, startOfYear, subYears } from "date-fns";
import { BusinessTaxReturns, FirmSize } from "@common/types/entity";

export const ENTERPRISE_TRIAL_CALL_URL =
    "https://calendly.com/d/cv28-cyy-56c/kick-introduction-call";

export const FRESHBOOKS_TRIAL_CALL_URL = `https://calendly.com/d/cybx-mfs-ds7/freshbooks-intro-call`;

// Shown for firms with 20-200 bookkeeping clients.
export const ACCOUNTANT_PARTNER_CALL_URL =
    "https://calendly.com/d/ctjc-pqq-3c8/kick-accountant-program";

export const ACCOUNTANT_ENTERPRISE_CALL_URL =
    "https://calendly.com/lucas-kick/introcall";

export const ACCOUNTANT_PLATFORM_CALL_URL =
    "https://calendly.com/ben-kick/kick-accountant";

export enum AccountantOnboardingRoute {
    SELF_SERVE = "SELF_SERVE",
    PARTNER = "PARTNER",
    ENTERPRISE = "ENTERPRISE",
    PLATFORM = "PLATFORM",
}

export type AccountantCallRoute = Exclude<
    AccountantOnboardingRoute,
    AccountantOnboardingRoute.SELF_SERVE
>;

interface AccountantRoutingProfile {
    firmSize?: FirmSize | null;
    businessTaxReturns?: BusinessTaxReturns | null;
}

const PARTNER_FIRM_SIZES: FirmSize[] = [
    FirmSize["20_49"],
    FirmSize["50_99"],
    FirmSize["100_199"],
];

/**
 * Accountant onboarding routing. Tax clients take precedence:
 * 2500+ -> Platform, 1000-2499 -> Enterprise. Otherwise bookkeeping clients:
 * <20 -> Self serve, 20-199 -> Partner, 200+ -> Enterprise. Firm size buckets
 * align with the cutoffs.
 */
export function getAccountantOnboardingRoute(
    profile: AccountantRoutingProfile | null | undefined,
): AccountantOnboardingRoute {
    const firmSize = profile?.firmSize;
    const businessTaxReturns = profile?.businessTaxReturns;
    if (businessTaxReturns === BusinessTaxReturns["2500_PLUS"]) {
        return AccountantOnboardingRoute.PLATFORM;
    }
    if (businessTaxReturns === BusinessTaxReturns["1000_2499"]) {
        return AccountantOnboardingRoute.ENTERPRISE;
    }
    if (!firmSize) {
        return AccountantOnboardingRoute.SELF_SERVE;
    }
    if (PARTNER_FIRM_SIZES.includes(firmSize)) {
        return AccountantOnboardingRoute.PARTNER;
    }
    if (firmSize === FirmSize["200_499"] || firmSize === FirmSize["500_PLUS"]) {
        return AccountantOnboardingRoute.ENTERPRISE;
    }
    return AccountantOnboardingRoute.SELF_SERVE;
}

export function getAccountantCallUrl(route: AccountantCallRoute): string {
    switch (route) {
        case AccountantOnboardingRoute.PARTNER:
            return ACCOUNTANT_PARTNER_CALL_URL;
        case AccountantOnboardingRoute.ENTERPRISE:
            return ACCOUNTANT_ENTERPRISE_CALL_URL;
        case AccountantOnboardingRoute.PLATFORM:
            return ACCOUNTANT_PLATFORM_CALL_URL;
    }
}

// Option values of the "How many bookkeeping clients does your firm serve?"
// select on the AI-Native Program demo form. Calendly only prefills a select
// when the value matches an option exactly. The separator is an en dash,
// written as \u2013 so editors and formatters can't turn it into a hyphen.
// Buckets that span several options leave the answer blank.
const ACCOUNTANT_DEMO_CLIENT_COUNT_OPTIONS = {
    [FirmSize["1_19"]]: "",
    [FirmSize["20_49"]]: "20\u201350",
    [FirmSize["50_99"]]: "51\u201399",
    [FirmSize["100_199"]]: "100\u2013299",
    [FirmSize["200_499"]]: "",
    [FirmSize["500_PLUS"]]: "300+",
} as const satisfies Record<FirmSize, string>;

export function getAccountantDemoClientCountAnswer(
    firmSize: FirmSize | null | undefined,
): string {
    return firmSize ? ACCOUNTANT_DEMO_CLIENT_COUNT_OPTIONS[firmSize] : "";
}

export const PARTNER_INTRO_CALL_URL =
    "https://calendly.com/echo_kick/kick-bookkeeper-dec24";

export const ONBOARDING_CALL_URL =
    "https://calendly.com/d/cv28-cyy-56c/kick-introduction-call";

export enum SubscriptionStatus {
    ACTIVE = "ACTIVE",
    CANCELLED = "CANCELLED",
    INCOMPLETE = "INCOMPLETE",
    INCOMPLETE_EXPIRED = "INCOMPLETE_EXPIRED",
    PAST_DUE = "PAST_DUE",
    PAUSED = "PAUSED",
    TRIALING = "TRIALING",
    UNPAID = "UNPAID",
}

export const ACTIVE_SUBSCRIPTION_STATUSES = [
    SubscriptionStatus.ACTIVE,
    SubscriptionStatus.TRIALING,
    SubscriptionStatus.PAST_DUE,
];

export function isActiveSubscriptionStatus(
    status: SubscriptionStatus | null | undefined,
): boolean {
    return status != null && ACTIVE_SUBSCRIPTION_STATUSES.includes(status);
}

export function didBecomePaid(
    previous: SubscriptionStatus | null,
    next: SubscriptionStatus | null,
): boolean {
    return (
        !isActiveSubscriptionStatus(previous) &&
        isActiveSubscriptionStatus(next)
    );
}

/**
 * Statuses Stripe treats as end-of-life for a subscription: it is gone and can
 * never bill again, so there is no upcoming invoice to preview.
 */
export const TERMINAL_SUBSCRIPTION_STATUSES = [
    SubscriptionStatus.CANCELLED,
    SubscriptionStatus.INCOMPLETE_EXPIRED,
];

export function isTerminalSubscriptionStatus(
    status: SubscriptionStatus | null | undefined,
): boolean {
    return status != null && TERMINAL_SUBSCRIPTION_STATUSES.includes(status);
}

export enum StripeProrationBehavior {
    ALWAYS_INVOICE = "always_invoice",
    CREATE_PRORATIONS = "create_prorations",
    NONE = "none",
}

/**
 * Platform default, mirroring the `organization.stripeProrationBehavior`
 * column default. Used on subscription paths that have no owning
 * organization to resolve the override from (workspace-owned billing).
 * `StripeService` writers take the behaviour as a required argument — this
 * constant is never applied implicitly.
 */
export const DEFAULT_STRIPE_PRORATION_BEHAVIOR =
    StripeProrationBehavior.ALWAYS_INVOICE;

export enum InvoiceStatus {
    DRAFT = "DRAFT",
    OPEN = "OPEN",
    PAID = "PAID",
    VOID = "VOID",
    UNCOLLECTIBLE = "UNCOLLECTIBLE",
}

export const DEFAULT_FREE_SAVINGS_CREDITS = 10_000;
export const DEFAULT_FREE_USAGE_LIMIT = 25_000;

export type DiscountType = "flat" | "percent";
export type BillingInterval = "day" | "month" | "week" | "year";

export interface BillingDateFilters {
    start: Date;
    end: Date;
}

export function currentYearFilters() {
    return {
        start: startOfYear(new Date()),
        end: endOfMonth(new Date()),
    };
}

export function previousYearFilters() {
    return {
        start: subYears(startOfYear(new Date()), 1),
        end: subYears(endOfYear(new Date()), 1),
    };
}

export function getBillingIntervalAdjective(
    interval: BillingInterval,
    count: number,
): string {
    if (interval === "year") {
        return "annually";
    }

    if (interval === "month" && count === 1) {
        return "monthly";
    }

    if (interval === "month" && count === 3) {
        return "quarterly";
    }

    return `every ${count} months`;
}

export function getMonthlyAmount(
    unitAmount: number,
    interval: BillingInterval,
    intervalCount: number,
): number {
    if (interval === "year") {
        return unitAmount / 12;
    }

    return unitAmount / intervalCount;
}

export function getAnnualAmount(
    unitAmount: number,
    interval: BillingInterval,
    intervalCount: number,
) {
    if (interval === "year") {
        return unitAmount;
    }

    return (12 / intervalCount) * unitAmount;
}
